import React, {useEffect, useMemo, useState} from 'react';
import {RouteComponentProps} from 'react-router-dom';
import {css} from 'emotion';
import Button from '@amzn/meridian/button';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import SiteDetailsTable from './SiteDetailsTable';
import {ConstraintValueRecord, DateIdMap} from '../utils/analyzeConstraints';
import {getConstraint} from '../../../common/apis/BackendApisClient';
import {useAppSelector} from '../../../app/hooks';
import {selectCachedBusiness, selectCachedCountry, selectCachedRegion} from '../../cached-state/cachedStateSlice';
import {getLocalizedAPIsStartDate} from '../../../common/utils/dates';
import PageLoading from '../../../common/components/PageLoading';

const DAYS_PER_PAGE = 14;
const TOTAL_WEEKS = 18;
const TOTAL_DAYS = TOTAL_WEEKS * 7;

interface IProps {
  history: RouteComponentProps['history'];
  dailyConstraints: ConstraintValueRecord[];
  highlightedDates: DateIdMap;
  siteId: string;
}

const toggleRowStyle = css`
  padding-bottom: 8px;
`;

const SiteDailyConstraintTable = ({history, dailyConstraints, highlightedDates, siteId}: IProps) => {
  const [viewMode, setViewMode] = useState<'daily' | 'weekly'>('daily');
  const [dailyData, setDailyData] = useState<ConstraintValueRecord[] | null>(null);
  const [loadingDaily, setLoadingDaily] = useState(false);
  const [dayOffset, setDayOffset] = useState(0);

  // Expand weekly highlightedDates to cover all 7 days of each week
  const dailyHighlightedDates: DateIdMap = useMemo(() => {
    const result: DateIdMap = {};
    Object.keys(highlightedDates).forEach((weekKey) => {
      const [y, m, d] = weekKey.split('-').map(Number);
      const weekStart = new Date(y, m - 1, d);
      for (let i = 0; i < 7; i++) {
        const day = new Date(weekStart);
        day.setDate(day.getDate() + i);
        const dayKey = `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, '0')}-${String(day.getDate()).padStart(
          2,
          '0'
        )}`;
        result[dayKey] = highlightedDates[weekKey];
      }
    });
    return result;
  }, [highlightedDates]);

  const businessType = useAppSelector(selectCachedBusiness);
  const region = useAppSelector(selectCachedRegion);
  const country = useAppSelector(selectCachedCountry);

  const currDate = new Date();
  const startTime = getLocalizedAPIsStartDate(currDate);

  const constraintIds = dailyConstraints.map((c) => c.constraintId).join(',');

  // Reset when inputs change
  useEffect(() => {
    setDailyData(null);
    setDayOffset(0);
  }, [constraintIds, businessType, region, country, siteId]);

  // Fetch daily data from getConstraint API
  useEffect(() => {
    if (viewMode === 'daily' && !dailyData && businessType && region) {
      setLoadingDaily(true);

      const fetchDailyData = async () => {
        const rows: ConstraintValueRecord[] = [];

        const results = await Promise.allSettled(
          dailyConstraints.map((constraint) =>
            getConstraint({
              businessType: businessType!,
              region: region!,
              country: country ?? undefined,
              constraintId: Number(constraint.constraintId),
              startTime,
              query: siteId,
            })
          )
        );

        for (let i = 0; i < dailyConstraints.length; i++) {
          const result = results[i];
          if (result.status === 'fulfilled' && result.value && result.value.valuesData && result.value.valuesData.length > 0) {
            const constraint = dailyConstraints[i];
            const siteRow = result.value.valuesData.find((r) => r.siteId === siteId);
            if (siteRow) {
              const dailyRecord: ConstraintValueRecord = {
                constraintId: constraint.constraintId,
                constraintName: constraint.constraintName,
                constraintValueType: constraint.constraintValueType as string,
                constraintUsageTags: constraint.constraintUsageTags as string[],
                constraint: constraint.constraint,
              };

              // Generate all daily dates for 18 weeks
              const startDate = new Date(startTime);
              for (let d = 0; d < TOTAL_DAYS; d++) {
                const current = new Date(startDate);
                current.setDate(current.getDate() + d);
                const key = `${current.getFullYear()}-${String(current.getMonth() + 1).padStart(2, '0')}-${String(
                  current.getDate()
                ).padStart(2, '0')}`;
                dailyRecord[key] = siteRow[key] !== undefined && siteRow[key] !== null ? siteRow[key] : '';
              }

              rows.push(dailyRecord);
            }
          }
        }

        setDailyData(rows);
        setLoadingDaily(false);
      };

      fetchDailyData().catch(() => {
        setDailyData([]);
        setLoadingDaily(false);
      });
    }
  }, [viewMode, dailyData, dailyConstraints, businessType, region, country, siteId, startTime]);

  if (!dailyConstraints || dailyConstraints.length === 0) {
    return null;
  }

  // Page the daily data to show only DAYS_PER_PAGE columns
  const getPagedDailyData = (): ConstraintValueRecord[] => {
    if (!dailyData) return [];
    const startDate = new Date(startTime);
    startDate.setDate(startDate.getDate() + dayOffset * 7);

    const pageDayKeys: string[] = [];
    for (let i = 0; i < DAYS_PER_PAGE; i++) {
      const d = new Date(startDate);
      d.setDate(d.getDate() + i);
      pageDayKeys.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);
    }

    return dailyData.map((row) => {
      const pagedRow: ConstraintValueRecord = {
        constraintId: row.constraintId,
        constraintName: row.constraintName,
        constraintValueType: row.constraintValueType as string,
        constraintUsageTags: row.constraintUsageTags as string[],
        constraint: row.constraint,
      };
      pageDayKeys.forEach((key) => {
        pagedRow[key] = row[key] !== undefined ? row[key] : '';
      });
      return pagedRow;
    });
  };

  // Date range label for daily view
  const rangeStartDate = new Date(startTime);
  rangeStartDate.setDate(rangeStartDate.getDate() + dayOffset * 7);
  const rangeEndDate = new Date(rangeStartDate);
  rangeEndDate.setDate(rangeEndDate.getDate() + DAYS_PER_PAGE - 1);
  const rangeLabel = `${rangeStartDate.toLocaleString('default', {month: 'short', day: '2-digit'})} - ${rangeEndDate.toLocaleString(
    'default',
    {month: 'short', day: '2-digit'}
  )}`;

  if (viewMode === 'daily' && loadingDaily) {
    return (
      <div>
        <Row className={toggleRowStyle} spacing="small">
          <Button type="primary" size="small">
            Daily Aggregate
          </Button>
          <Button type="tertiary" size="small" onClick={() => setViewMode('weekly')}>
            Weekly Aggregate
          </Button>
        </Row>
        <PageLoading />
      </div>
    );
  }

  const displayData = viewMode === 'daily' ? getPagedDailyData() : dailyConstraints;

  return (
    <div>
      <Row className={toggleRowStyle} spacing="small">
        <Button
          type={viewMode === 'daily' ? 'primary' : 'tertiary'}
          size="small"
          onClick={() => {
            setViewMode('daily');
            setDayOffset(0);
          }}
        >
          Daily Aggregate
        </Button>
        <Button type={viewMode === 'weekly' ? 'primary' : 'tertiary'} size="small" onClick={() => setViewMode('weekly')}>
          Weekly Aggregate
        </Button>
      </Row>
      {viewMode === 'daily' && (
        <Row className={toggleRowStyle} spacing="small" alignmentVertical="center">
          <Button type="tertiary" size="small" onClick={() => setDayOffset(Math.max(0, dayOffset - 2))} disabled={dayOffset === 0}>
            ← Prev
          </Button>
          <Text type="b200">{rangeLabel}</Text>
          <Button
            type="tertiary"
            size="small"
            onClick={() => setDayOffset(dayOffset + 2)}
            disabled={(dayOffset + 2) * 7 + DAYS_PER_PAGE > TOTAL_DAYS}
          >
            Next →
          </Button>
        </Row>
      )}
      <SiteDetailsTable
        key={`${viewMode}-${dayOffset}`}
        history={history}
        allowSorting={viewMode === 'weekly'}
        allowPaging={viewMode === 'weekly'}
        allowFiltering={false}
        constraintData={displayData}
        highlightedDates={viewMode === 'daily' ? dailyHighlightedDates : highlightedDates}
        summaryRecords={[]}
      />
    </div>
  );
};

export default SiteDailyConstraintTable;
